import LogManager from "../../../utils/logManager";
import { callRouterLambda } from "../../../utils/routerLambdaUtils";
import { VERBOSITY_LEVELS } from "../../../constants";

const STORAGE_KEY = "aims_preference_config";

/**
 * Get the current user's full preference config.
 * Returns cached value if available, otherwise fetches from backend.
 * @returns {Promise<object>} preferenceConfig object with all set preferences
 */
const getPreference = async () => {
  const cached = sessionStorage.getItem(STORAGE_KEY);
  if (cached) return JSON.parse(cached);

  try {
    const config = await callRouterLambda("/preferences", { action: "get" });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    return config;
  } catch (error) {
    LogManager.error("Error calling /preferences get:", error);
    LogManager.metric("Preference_Call_Failed", 1, { operation: "get" });
    return {};
  }
};

/**
 * Update the current user's preferences. Only send the fields you want to change —
 * the backend merges with existing preferences (does not wipe unset fields).
 * @param {object} preferenceConfig - partial config with only the fields to update
 * @returns {Promise<object>} the confirmed preferenceConfig from the backend
 */
const updatePreference = async (preferenceConfig) => {
  try {
    const config = await callRouterLambda("/preferences", { action: "update", preferenceConfig });
    // Merge into cache (preserves fields not in this update)
    const cached = sessionStorage.getItem(STORAGE_KEY);
    const merged = { ...(cached ? JSON.parse(cached) : {}), ...config };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    return config;
  } catch (error) {
    LogManager.error("Error calling /preferences update:", error);
    LogManager.metric("Preference_Call_Failed", 1, { operation: "update" });
    throw error;
  }
};

/**
 * Convenience: get just the verbosity level from preferences.
 * @returns {Promise<string>} "concise" | "moderate" | "detailed"
 */
export const getVerbosityLevel = async () => {
  const config = await getPreference();
  return config.verbosityLevel || VERBOSITY_LEVELS.CONCISE;
};

/**
 * Convenience: update just the verbosity level.
 * @param {string} verbosityLevel - "concise", "moderate", or "detailed"
 */
export const updateVerbosityLevel = async (verbosityLevel) => {
  return updatePreference({ verbosityLevel });
};

/**
 * Convenience: get the auto-start summarization preference.
 * @returns {Promise<boolean>} true if auto-start is enabled
 */
export const getAutoStart = async () => {
  const config = await getPreference();
  return config.autoStart === true;
};

/**
 * Convenience: update the auto-start summarization preference.
 * @param {boolean} enabled - whether auto-start should be enabled
 */
export const updateAutoStart = async (enabled) => {
  return updatePreference({ autoStart: enabled });
};
